import { useState } from 'react';

function Contact({ darkMode }) {
  const [activeTab, setActiveTab] = useState('message'); // 'message' or 'customize'
  const [isVisible, setIsVisible] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [customizationSent, setCustomizationSent] = useState(false);
  
  // Message Form State
  const [messageForm, setMessageForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  // Customization Form State
  const [customForm, setCustomForm] = useState({
    name: '',
    email: '',
    projectType: '',
    style: '',
    colors: [],
    features: [],
    budget: '',
    timeline: '',
    additionalInfo: ''
  });

  useState(() => {
    setIsVisible(true);
  }, []);

  const projectTypes = [
    { id: 'website', name: 'Website', icon: '🌐', description: 'Business website or portfolio' },
    { id: 'webapp', name: 'Web App', icon: '💻', description: 'Interactive web application' },
    { id: 'ecommerce', name: 'E-Commerce', icon: '🛒', description: 'Online store or marketplace' },
    { id: 'mobile', name: 'Mobile App', icon: '📱', description: 'iOS/Android application' },
    { id: 'custom', name: 'Custom Solution', icon: '🔧', description: 'Something unique and special' }
  ];

  const styleOptions = [
    { id: 'modern', name: 'Modern & Minimal', preview: 'Clean lines, lots of whitespace' },
    { id: 'bold', name: 'Bold & Vibrant', preview: 'Eye-catching colors and animations' },
    { id: 'professional', name: 'Professional', preview: 'Corporate and trustworthy' },
    { id: 'creative', name: 'Creative & Artistic', preview: 'Unique and expressive design' },
    { id: 'dark', name: 'Dark Theme', preview: 'Sleek dark interface' }
  ];

  const colorPalettes = [
    { id: 'blue', name: 'Ocean Blue', colors: ['#3B82F6', '#1E40AF', '#60A5FA'] },
    { id: 'purple', name: 'Royal Purple', colors: ['#8B5CF6', '#7C3AED', '#A78BFA'] },
    { id: 'green', name: 'Nature Green', colors: ['#10B981', '#059669', '#34D399'] },
    { id: 'pink', name: 'Sunset Pink', colors: ['#EC4899', '#DB2777', '#F472B6'] },
    { id: 'orange', name: 'Energy Orange', colors: ['#F59E0B', '#D97706', '#FBBF24'] },
    { id: 'gradient', name: 'Multi-Gradient', colors: ['#FF006E', '#8338EC', '#3A86FF'] }
  ];

  const featureOptions = [
    'Responsive Design', 'User Authentication', 'Payment Integration', 'Admin Dashboard',
    'Real-time Updates', 'API Integration', 'SEO Optimization', 'Analytics',
    'Multi-language Support', 'Social Media Integration', 'Email Marketing', 'Chat Support'
  ];

  const handleMessageSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the message to your backend
    console.log('Message submitted:', messageForm);
    setMessageSent(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setMessageSent(false);
      setMessageForm({ name: '', email: '', subject: '', message: '' });
    }, 3000);
  };

  const handleCustomizationSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the customization to your backend
    console.log('Customization submitted:', customForm);
    setCustomizationSent(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setCustomizationSent(false);
      setCustomForm({
        name: '', email: '', projectType: '', style: '', colors: [],
        features: [], budget: '', timeline: '', additionalInfo: ''
      });
    }, 3000);
  };

  const updateMessageForm = (field, value) => {
    setMessageForm(prev => ({ ...prev, [field]: value }));
  };

  const updateCustomForm = (field, value) => {
    setCustomForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleFeature = (feature) => {
    setCustomForm(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  const toggleColor = (colorId) => {
    setCustomForm(prev => ({
      ...prev,
      colors: prev.colors.includes(colorId)
        ? prev.colors.filter(c => c !== colorId)
        : [...prev.colors, colorId]
    }));
  };

  return (
    <div className={`min-h-screen transition-all duration-700 ${
      darkMode 
        ? 'bg-gradient-to-br from-slate-900 via-purple-900 to-slate-800' 
        : 'bg-gradient-to-br from-slate-800 via-purple-800 to-slate-700'
    }`}>
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-96 h-96 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-4000"></div>
      </div>

      <div className="relative z-10 container mx-auto px-6 py-20">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h1 className="text-6xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 mb-4 tracking-tight">
            Let's Work Together
          </h1>
          <div className="h-1 w-32 bg-gradient-to-r from-cyan-400 to-purple-600 rounded-full mx-auto mb-6"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Ready to bring your ideas to life? Choose how you'd like to get started below.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className={`flex justify-center mb-12 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="bg-white/10 backdrop-blur-sm rounded-full p-2 border border-white/20">
            <button
              onClick={() => setActiveTab('message')}
              className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 ${
                activeTab === 'message'
                  ? 'bg-gradient-to-r from-cyan-500 to-purple-600 text-white shadow-lg'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              💌 Send Message
            </button>
            <button
              onClick={() => setActiveTab('customize')}
              className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 ${
                activeTab === 'customize'
                  ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-lg'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              🎨 Customize Project
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={`transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          
          {/* Message Tab */}
          {activeTab === 'message' && (
            <div className="max-w-2xl mx-auto">
              {!messageSent ? (
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-8 shadow-2xl">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-white mb-4">Drop Me a Message</h2>
                    <p className="text-gray-300">Have a question or want to discuss a project? I'd love to hear from you!</p>
                  </div>

                  <form onSubmit={handleMessageSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-white font-semibold mb-2">Name *</label>
                        <input
                          type="text"
                          required
                          value={messageForm.name}
                          onChange={(e) => updateMessageForm('name', e.target.value)}
                          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:border-cyan-400 focus:outline-none transition-colors duration-300"
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label className="block text-white font-semibold mb-2">Email *</label>
                        <input
                          type="email"
                          required
                          value={messageForm.email}
                          onChange={(e) => updateMessageForm('email', e.target.value)}
                          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:border-cyan-400 focus:outline-none transition-colors duration-300"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-white font-semibold mb-2">Subject *</label>
                      <input
                        type="text"
                        required
                        value={messageForm.subject}
                        onChange={(e) => updateMessageForm('subject', e.target.value)}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:border-cyan-400 focus:outline-none transition-colors duration-300"
                        placeholder="What's this about?"
                      />
                    </div>

                    <div>
                      <label className="block text-white font-semibold mb-2">Message *</label>
                      <textarea
                        required
                        rows="6"
                        value={messageForm.message}
                        onChange={(e) => updateMessageForm('message', e.target.value)}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:border-cyan-400 focus:outline-none transition-colors duration-300 resize-none"
                        placeholder="Tell me about your project, question, or just say hello!"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full py-4 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg font-bold text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                    >
                      Send Message 🚀
                    </button>
                  </form>
                </div>
              ) : (
                <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 backdrop-blur-sm rounded-2xl border border-green-500/30 p-8 text-center">
                  <div className="text-6xl mb-4">✅</div>
                  <h3 className="text-2xl font-bold text-white mb-4">Message Sent!</h3>
                  <p className="text-gray-300">Thanks for reaching out! I'll get back to you within 24 hours.</p>
                </div>
              )}
            </div>
          )}

          {/* Customize Tab */}
          {activeTab === 'customize' && (
            <div className="max-w-4xl mx-auto">
              {!customizationSent ? (
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-8 shadow-2xl">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-white mb-4">Customize Your Project</h2>
                    <p className="text-gray-300">Let's create something amazing together! Tell me about your vision.</p>
                  </div>

                  <form onSubmit={handleCustomizationSubmit} className="space-y-8">
                    {/* Basic Info */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-white font-semibold mb-2">Name *</label>
                        <input
                          type="text"
                          required
                          value={customForm.name}
                          onChange={(e) => updateCustomForm('name', e.target.value)}
                          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:border-purple-400 focus:outline-none transition-colors duration-300"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label className="block text-white font-semibold mb-2">Email *</label>
                        <input
                          type="email"
                          required
                          value={customForm.email}
                          onChange={(e) => updateCustomForm('email', e.target.value)}
                          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:border-purple-400 focus:outline-none transition-colors duration-300"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    {/* Project Type */}
                    <div>
                      <label className="block text-white font-semibold mb-4">Project Type *</label>
                      <div className="grid md:grid-cols-3 gap-4">
                        {projectTypes.map((type) => (
                          <button
                            key={type.id}
                            type="button"
                            onClick={() => updateCustomForm('projectType', type.id)}
                            className={`p-4 rounded-lg border-2 transition-all duration-300 text-left ${
                              customForm.projectType === type.id
                                ? 'border-purple-400 bg-purple-500/20'
                                : 'border-white/20 bg-white/5 hover:border-white/40'
                            }`}
                          >
                            <div className="text-2xl mb-2">{type.icon}</div>
                            <h4 className="text-white font-semibold">{type.name}</h4>
                            <p className="text-gray-400 text-sm">{type.description}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Style Preference */}
                    <div>
                      <label className="block text-white font-semibold mb-4">Design Style</label>
                      <div className="space-y-3">
                        {styleOptions.map((style) => (
                          <label
                            key={style.id}
                            className={`flex items-center p-4 rounded-lg cursor-pointer transition-all duration-300 ${
                              customForm.style === style.id
                                ? 'bg-purple-500/20 border-2 border-purple-400'
                                : 'bg-white/5 border-2 border-transparent hover:bg-white/10'
                            }`}
                          >
                            <input
                              type="radio"
                              name="style"
                              value={style.id}
                              checked={customForm.style === style.id}
                              onChange={(e) => updateCustomForm('style', e.target.value)}
                              className="sr-only"
                            />
                            <div className="flex-1">
                              <h4 className="text-white font-semibold">{style.name}</h4>
                              <p className="text-gray-400 text-sm">{style.preview}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Color Palette */}
                    <div>
                      <label className="block text-white font-semibold mb-4">Color Palette (Select multiple)</label>
                      <div className="grid md:grid-cols-3 gap-4">
                        {colorPalettes.map((palette) => (
                          <button
                            key={palette.id}
                            type="button"
                            onClick={() => toggleColor(palette.id)}
                            className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                              customForm.colors.includes(palette.id)
                                ? 'border-purple-400 bg-purple-500/20'
                                : 'border-white/20 bg-white/5 hover:border-white/40'
                            }`}
                          >
                            <div className="flex space-x-2 mb-3">
                              {palette.colors.map((color, index) => (
                                <div
                                  key={index}
                                  className="w-6 h-6 rounded-full"
                                  style={{ backgroundColor: color }}
                                />
                              ))}
                            </div>
                            <p className="text-white font-semibold text-sm">{palette.name}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Features */}
                    <div>
                      <label className="block text-white font-semibold mb-4">Desired Features</label>
                      <div className="grid md:grid-cols-3 gap-3">
                        {featureOptions.map((feature) => (
                          <label
                            key={feature}
                            className={`flex items-center p-3 rounded-lg cursor-pointer transition-all duration-300 ${
                              customForm.features.includes(feature)
                                ? 'bg-purple-500/20 border border-purple-400'
                                : 'bg-white/5 border border-transparent hover:bg-white/10'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={customForm.features.includes(feature)}
                              onChange={() => toggleFeature(feature)}
                              className="sr-only"
                            />
                            <span className="text-white text-sm">{feature}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Budget & Timeline */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-white font-semibold mb-2">Budget Range</label>
                        <select
                          value={customForm.budget}
                          onChange={(e) => updateCustomForm('budget', e.target.value)}
                          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:border-purple-400 focus:outline-none transition-colors duration-300"
                        >
                          <option value="">Select budget range</option>
                          <option value="under-1k">Under $1,000</option>
                          <option value="1k-5k">$1,000 - $5,000</option>
                          <option value="5k-10k">$5,000 - $10,000</option>
                          <option value="10k-plus">$10,000+</option>
                          <option value="discuss">Let's discuss</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-white font-semibold mb-2">Timeline</label>
                        <select
                          value={customForm.timeline}
                          onChange={(e) => updateCustomForm('timeline', e.target.value)}
                          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:border-purple-400 focus:outline-none transition-colors duration-300"
                        >
                          <option value="">Select timeline</option>
                          <option value="asap">ASAP</option>
                          <option value="1-month">Within 1 month</option>
                          <option value="2-3-months">2-3 months</option>
                          <option value="flexible">I'm flexible</option>
                        </select>
                      </div>
                    </div>

                    {/* Additional Info */}
                    <div>
                      <label className="block text-white font-semibold mb-2">Additional Information</label>
                      <textarea
                        rows="4"
                        value={customForm.additionalInfo}
                        onChange={(e) => updateCustomForm('additionalInfo', e.target.value)}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:border-purple-400 focus:outline-none transition-colors duration-300 resize-none"
                        placeholder="Any specific requirements, inspiration, or details you'd like to share..."
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg font-bold text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                    >
                      Submit Customization Request ✨
                    </button>
                  </form>
                </div>
              ) : (
                <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-sm rounded-2xl border border-purple-500/30 p-8 text-center">
                  <div className="text-6xl mb-4">🎉</div>
                  <h3 className="text-2xl font-bold text-white mb-4">Customization Received!</h3>
                  <p className="text-gray-300 mb-4">Awesome! I've received your project details and will create a custom proposal for you.</p>
                  <p className="text-purple-400 font-semibold">Expect a detailed response within 24 hours!</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Back to Portfolio Button */}
        <div className="text-center mt-16">
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-gray-300 hover:text-white hover:border-cyan-400 transition-all duration-300"
          >
            <svg className="mr-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m0 0v14" />
            </svg>
            Back to Portfolio
          </button>
        </div>
      </div>

      <style jsx>{`
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
}

export default Contact;